import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, IconButton, Paper, TextField, Tooltip, Typography } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { __t } from '../../../i18n/t';
import { HomeBlock, HomeItem, ROW_ART, ROW_LAYOUT_LABEL, ROW_TITLE_MAX, rowCapacity } from './homeItems';

const DRAG_HANDLE_SX = {
  display: 'flex', alignItems: 'center', cursor: 'grab',
  color: 'text.disabled', touchAction: 'none',
};

const SLOT_SX = {
  flex: 1, minWidth: 0, height: 86, borderRadius: 1, overflow: 'hidden',
  position: 'relative' as const, display: 'flex', alignItems: 'center',
  justifyContent: 'center', bgcolor: 'action.hover',
};

const MISSING_IMAGE_SX = {
  display: 'flex', flexDirection: 'column' as const,
  alignItems: 'center', color: 'warning.main',
};

const EMPTY_SLOT_SX = {
  ...SLOT_SX,
  border: '1px dashed',
  borderColor: 'divider',
  flexDirection: 'column' as const,
  gap: 0.25,
};

interface BlockCardProps {
  block: HomeBlock;
  ratio: number;
  onEdit(): void;
  onRemove(): void;
}

/** Um bloco dentro da linha. Arrastável para outra linha, ou para o outro lado desta. */
const BlockCard: React.FC<BlockCardProps> = ({ block, ratio, onEdit, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id, data: { type: 'block' } });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        ...SLOT_SX,
        opacity: isDragging ? 0.35 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
        border: '1px solid',
        borderColor: block.imagePath ? 'divider' : 'warning.light',
      }}
    >
      {block.imagePath ? (
        <Box component="img" src={block.imagePath} alt=""
          sx={{ width: '100%', height: '100%', objectFit: 'cover', aspectRatio: String(ratio) }} />
      ) : (
        <Box sx={MISSING_IMAGE_SX}>
          <WarningAmberIcon fontSize="small" />
          <Typography variant="caption">Sem imagem</Typography>
        </Box>
      )}

      <Box sx={{
        position: 'absolute', top: 2, right: 2, display: 'flex', gap: 0.25,
        bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1,
      }}>
        <Tooltip title="Editar bloco">
          <IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title="Remover bloco">
          <IconButton size="small" onClick={onRemove}><DeleteIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>

      <Box {...attributes} {...listeners}
        sx={{ ...DRAG_HANDLE_SX, position: 'absolute', top: 4, left: 4, cursor: 'grab' }}>
        <DragIndicatorIcon fontSize="small" />
      </Box>
    </Box>
  );
};

interface RowContainerProps {
  row: HomeItem;
  onToggleVisible(): void;
  onChangeTitle(title: string): void;
  onRemoveRow(): void;
  onEditBlock(blockId: string): void;
  onRemoveBlock(blockId: string): void;
  onAddBlock(): void;
}

const RowContainer: React.FC<RowContainerProps> = ({
  row, onToggleVisible, onChangeTitle, onRemoveRow, onEditBlock, onRemoveBlock, onAddBlock,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: row.id, data: { type: 'row' } });

  // Alvo de soltura da linha: é o que permite arrastar um bloco de outra linha para cá.
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: `row-drop-${row.id}`, data: { type: 'row-drop', rowId: row.id } });

  const blocks = row.blocks || [];
  const capacity = rowCapacity(row);
  const art = ROW_ART[row.layout || 'full'];
  const freeSlots = Math.max(0, capacity - blocks.length);
  // Montado fora do JSX: template literal em posição de texto vira entrada de catálogo,
  // e "%{arg1} · %{arg2}/%{arg3}" não é texto para traduzir.
  const summary = `${__t(ROW_LAYOUT_LABEL[row.layout || 'full'])} · ${blocks.length}/${capacity}`;

  return (
    <Paper
      ref={setNodeRef}
      variant="outlined"
      sx={{
        p: 1, mb: 0.75,
        opacity: isDragging ? 0.4 : (row.visible ? 1 : 0.55),
        transform: CSS.Transform.toString(transform),
        transition,
        borderColor: isOver ? 'primary.main' : 'divider',
        bgcolor: isOver ? 'action.selected' : 'background.paper',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box {...attributes} {...listeners} sx={DRAG_HANDLE_SX}>
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {summary}
        </Typography>
        <TextField
          size="small"
          variant="standard"
          value={row.title || ''}
          onChange={event => onChangeTitle(event.target.value)}
          placeholder="Título da linha (opcional)"
          inputProps={{ maxLength: ROW_TITLE_MAX }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <Tooltip title="Remover linha">
          <IconButton size="small" onClick={onRemoveRow}><DeleteIcon fontSize="small" /></IconButton>
        </Tooltip>
        <Tooltip title={row.visible ? 'Esconder da Home' : 'Mostrar na Home'}>
          <IconButton size="small" onClick={onToggleVisible}>
            {row.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>

      <Box ref={setDropRef} sx={{ display: 'flex', gap: 1 }}>
        <SortableContext items={blocks.map(b => b.id)} strategy={horizontalListSortingStrategy}>
          {blocks.map(block => (
            <BlockCard
              key={block.id}
              block={block}
              ratio={art.ratio}
              onEdit={() => onEditBlock(block.id)}
              onRemove={() => onRemoveBlock(block.id)}
            />
          ))}
        </SortableContext>

        {Array.from({ length: freeSlots }).map((_, index) => (
          <Box key={`free-${index}`} sx={EMPTY_SLOT_SX} onClick={onAddBlock}>
            <Typography variant="caption" color="text.secondary">Vaga livre</Typography>
            <Typography variant="caption" color="text.disabled">
              Arraste um bloco aqui ou clique
            </Typography>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

export default RowContainer;
